/**
 * Casca do painel do Admin (F1.8) — barra superior, navegação e conteúdo.
 *
 * O que esta casca garante, em todas as telas de dentro:
 *  - **quem está e onde está**: nome da pessoa, função e empresa sempre visíveis. Num painel que
 *    revoga aparelho e bloqueia gente, agir achando que é outro usuário é o pior erro possível;
 *  - **estado de conexão sempre à vista** (docs/03 §3 e tabela A, linha "sem internet"), com o
 *    mesmo texto do resto do produto — "Sem internet — tudo é salvo neste aparelho";
 *  - **navegação curta** (docs/03 §2): no mínimo da F1.8 são duas seções, Equipe e Configurações.
 *    A navegação é por âncora de hash — link de verdade, que funciona com teclado, com "abrir em
 *    nova aba" e sem biblioteca de rotas;
 *  - **nenhum botão de mesmo peso da ação principal da tela** (03 §3.5 regra i): por isso "Sair" é
 *    botão de texto, não botão em destaque.
 *
 * Sessão nula não é erro: é o painel ainda perguntando ao servidor quem é a pessoa. A barra mostra
 * "Carregando…" no lugar do nome em vez de piscar um painel vazio.
 */
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { UsuarioSessao } from '../auth/sessao.js';
import { ESTADOS } from '../estados/tipos.js';
import { Botao } from '../componentes/Botao.js';

export type SecaoPainel = 'equipe' | 'configuracoes';

interface ItemNavegacao {
  chave: SecaoPainel;
  rotulo: string;
  hash: string;
}

const NAVEGACAO: readonly ItemNavegacao[] = [
  { chave: 'equipe', rotulo: 'Equipe', hash: '#/equipe' },
  { chave: 'configuracoes', rotulo: 'Configurações', hash: '#/configuracoes' },
];

/** Hash → seção. Qualquer coisa desconhecida cai em "Equipe", que é a porta de entrada da F1.8. */
function lerSecaoDoHash(hash: string): SecaoPainel {
  return hash.includes('configuracoes') ? 'configuracoes' : 'equipe';
}

export interface LayoutProps {
  /** `null` enquanto o perfil ainda não voltou do servidor. */
  sessao: UsuarioSessao | null;
  empresaNome?: string | undefined;
  /** Seção controlada pela tela de fora. Sem isto, a casca segue o hash da barra de endereço. */
  secao?: SecaoPainel | undefined;
  aoNavegar?: ((secao: SecaoPainel) => void) | undefined;
  aoSair: () => void;
  /** Conexão controlada (útil em teste). Sem isto, a casca escuta o próprio navegador. */
  online?: boolean | undefined;
  children: ReactNode | ((secao: SecaoPainel) => ReactNode);
}

export function Layout({
  sessao,
  empresaNome,
  secao,
  aoNavegar,
  aoSair,
  online,
  children,
}: LayoutProps) {
  const [secaoDoEndereco, setSecaoDoEndereco] = useState<SecaoPainel>(() =>
    lerSecaoDoHash(window.location.hash),
  );
  const [conexaoDoNavegador, setConexaoDoNavegador] = useState<boolean>(() => navigator.onLine);
  const [focoNoAtalho, setFocoNoAtalho] = useState(false);

  useEffect(() => {
    const aoTrocarHash = () => setSecaoDoEndereco(lerSecaoDoHash(window.location.hash));
    window.addEventListener('hashchange', aoTrocarHash);
    return () => window.removeEventListener('hashchange', aoTrocarHash);
  }, []);

  useEffect(() => {
    // Escutar os dois eventos é o jeito barato de acertar o indicador; a fila local é quem de fato
    // resolve o offline — aqui só contamos a verdade para a pessoa.
    const conectou = () => setConexaoDoNavegador(true);
    const caiu = () => setConexaoDoNavegador(false);
    window.addEventListener('online', conectou);
    window.addEventListener('offline', caiu);
    return () => {
      window.removeEventListener('online', conectou);
      window.removeEventListener('offline', caiu);
    };
  }, []);

  const secaoAtual = secao ?? secaoDoEndereco;
  const conectado = online ?? conexaoDoNavegador;
  const nome = sessao?.nome ?? ESTADOS.carregando.texto;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cor-fundo)' }}>
      {/* Atalho para teclado: só aparece quando recebe foco, para não roubar espaço de ninguém. */}
      <a
        href="#conteudo"
        onFocus={() => setFocoNoAtalho(true)}
        onBlur={() => setFocoNoAtalho(false)}
        style={
          focoNoAtalho
            ? {
                position: 'absolute',
                insetInlineStart: 'var(--esp-3)',
                insetBlockStart: 'var(--esp-3)',
                zIndex: 'var(--z-toast)',
                background: 'var(--cor-superficie)',
                color: 'var(--cor-texto)',
                padding: 'var(--esp-2) var(--esp-3)',
                borderRadius: 'var(--raio-sm)',
                boxShadow: 'var(--sombra-md)',
                fontSize: 'var(--font-size-formulario)',
              }
            : {
                position: 'absolute',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
              }
        }
      >
        Pular para o conteúdo
      </a>

      <header
        style={{
          position: 'sticky',
          insetBlockStart: 0,
          zIndex: 'var(--z-barra)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--esp-4)',
          padding: 'var(--esp-3) var(--esp-4)',
          background: 'var(--cor-barra-escura)',
          color: 'var(--cor-barra-escura-texto)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span
            style={{
              color: 'var(--cor-superficie)',
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--peso-extra)',
              letterSpacing: 'var(--letra-titulo)',
            }}
          >
            AGROBOX Checklist
          </span>
          <span style={{ fontSize: 'var(--font-size-formulario)' }}>
            {empresaNome ?? ESTADOS.carregando.texto}
          </span>
        </div>

        <nav aria-label="Seções do painel" style={{ display: 'flex', gap: 'var(--esp-2)' }}>
          {NAVEGACAO.map((item) => {
            const ativo = item.chave === secaoAtual;
            return (
              <a
                key={item.chave}
                href={item.hash}
                aria-current={ativo ? 'page' : undefined}
                onClick={() => {
                  setSecaoDoEndereco(item.chave);
                  aoNavegar?.(item.chave);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 'var(--alvo-toque-min)',
                  padding: '0 var(--esp-3)',
                  borderRadius: 'var(--raio-sm)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-formulario)',
                  fontWeight: ativo ? 'var(--peso-forte)' : 'var(--peso-medio)',
                  color: ativo ? 'var(--cor-superficie)' : 'var(--cor-barra-escura-texto-2)',
                  background: ativo ? 'var(--cor-marca-escura)' : 'transparent',
                }}
              >
                {item.rotulo}
              </a>
            );
          })}
        </nav>

        <div
          style={{
            marginInlineStart: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--esp-4)',
            flexWrap: 'wrap',
          }}
        >
          <p
            // Mudança de conexão é anunciada: quem está no meio de um cadastro precisa saber que
            // ficou offline sem ter que olhar para o canto da tela.
            role="status"
            aria-live="polite"
            style={{
              margin: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--esp-2)',
              fontSize: 'var(--font-size-formulario)',
              color: conectado ? 'var(--cor-barra-escura-texto)' : 'var(--cor-superficie)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 'var(--esp-2)',
                height: 'var(--esp-2)',
                borderRadius: 'var(--raio-pilula)',
                background: conectado ? 'var(--cor-sucesso)' : 'var(--cor-alerta)',
              }}
            />
            {conectado ? 'Conectado' : ESTADOS.sem_internet.texto}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'end', minWidth: 0 }}>
            <span
              style={{
                color: 'var(--cor-superficie)',
                fontSize: 'var(--font-size-formulario)',
                fontWeight: 'var(--peso-medio)',
              }}
            >
              {nome}
            </span>
            {sessao ? (
              <span style={{ fontSize: 'var(--font-size-tabela)' }}>{sessao.funcao}</span>
            ) : null}
          </div>

          <Botao
            variante="texto"
            onClick={aoSair}
            style={{ color: 'var(--cor-barra-escura-texto)' }}
          >
            Sair
          </Botao>
        </div>
      </header>

      <main
        id="conteudo"
        tabIndex={-1}
        style={{
          maxWidth: 'var(--largura-conteudo-max)',
          margin: '0 auto',
          padding: 'var(--esp-6) var(--esp-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--esp-5)',
        }}
      >
        {typeof children === 'function' ? children(secaoAtual) : children}
      </main>
    </div>
  );
}
